import { useQuery } from '@tanstack/react-query';
import { animate, motion, useMotionValue, useTransform } from 'framer-motion';
import {
  ArrowRight,
  Banknote,
  CarFront,
  Fuel,
  Gift,
  MapPin,
  Receipt,
  ScanLine,
  ShieldCheck,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { BannerCarousel, HeroFallback } from './banner-carousel';

import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth-context';
import { formatarData, formatBrl } from '@/lib/format';
import { supabase } from '@/lib/supabase';
import { useEnsureOnboarding } from '@/lib/use-ensure-onboarding';
import { useFeatureFlag } from '@/lib/use-feature-flag';

/**
 * CountUp — contador animado 0 → valor final (Apple Wallet vibe).
 * Usa framer-motion para spring suave; respeita prefers-reduced-motion implicitamente
 * via duração curta + ease premium.
 */
function CountUp({ value }: { value: number }) {
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (v) => Math.round(v));
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const controls = animate(mv, value, {
      duration: 0.9,
      ease: [0.16, 1, 0.3, 1],
    });
    const unsub = rounded.on('change', (v) => setDisplay(v));
    return () => {
      controls.stop();
      unsub();
    };
  }, [value, mv, rounded]);

  return <>{display}</>;
}

interface DashCliente {
  nom_cliente: string;
  qtd_veiculos: number;
  proximo_vencimento: string | null;
  val_proxima_cobranca: number | null;
  qtd_lavagens_mes: number;
  nom_plano: string | null;
}

export function HomePage() {
  const { user } = useAuth();

  // Garante que cliente que entrou direto na home (ex: confirmacao de email,
  // OAuth Google) passe pelo onboarding se ainda nao concluiu.
  useEnsureOnboarding();

  const dash = useQuery({
    queryKey: ['cliente-home', user?.id],
    queryFn: async (): Promise<DashCliente> => {
      const { data: cli } = await supabase
        .from('tab_clientes')
        .select('seq_cliente, nom_cliente')
        .eq('cod_auth_user', user!.id)
        .single();
      const [veic, ass, lav] = await Promise.all([
        supabase
          .from('tab_veiculos')
          .select('seq_veiculo', { count: 'exact', head: true })
          .eq('seq_cliente', cli?.seq_cliente ?? 0)
          .eq('ind_ativo', true),
        supabase
          .from('tab_assinaturas')
          .select('dta_proximo_vencimento, val_mensalidade_atual, tab_planos(nom_plano)')
          .eq('seq_cliente', cli?.seq_cliente ?? 0)
          .eq('cod_status', 'ATIVA')
          .order('dta_inicio', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('tab_lavagens')
          .select('seq_lavagem', { count: 'exact', head: true })
          .gte(
            'dta_inicio',
            new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString(),
          ),
      ]);
      const planoEmbutido = ass.data as { tab_planos?: { nom_plano?: string } } | null;
      return {
        nom_cliente: cli?.nom_cliente ?? '',
        qtd_veiculos: veic.count ?? 0,
        proximo_vencimento: ass.data?.dta_proximo_vencimento ?? null,
        val_proxima_cobranca: ass.data?.val_mensalidade_atual ?? null,
        qtd_lavagens_mes: lav.count ?? 0,
        nom_plano: planoEmbutido?.tab_planos?.nom_plano ?? null,
      };
    },
    enabled: !!user,
  });

  // Sprint 11: atalho Abastecer condicional via feature flag MODULO_COMBUSTIVEL
  const combustivelAtivo = useFeatureFlag('MODULO_COMBUSTIVEL');

  const acoes = [
    {
      label: 'Comprar avulsa',
      href: '/avulso/comprar',
      icon: Banknote,
      sub: 'PIX rápido',
      cor: 'from-emerald-500 to-teal-600',
    },
    ...(combustivelAtivo
      ? [
          {
            label: 'Abastecer',
            href: '/abastecer',
            icon: Fuel,
            sub: 'pontos por litro',
            cor: 'from-amber-500 to-orange-700',
          },
        ]
      : []),
    {
      label: 'Meus veículos',
      href: '/veiculos',
      icon: CarFront,
      sub: 'gerenciar frota',
      cor: 'from-sky-500 to-blue-700',
    },
    {
      label: 'Mapa',
      href: '/mapa',
      icon: MapPin,
      sub: 'unidades próximas',
      cor: 'from-rose-500 to-pink-600',
    },
    {
      label: 'Histórico',
      href: '/lavagens',
      icon: Receipt,
      sub: 'todas as lavagens',
      cor: 'from-violet-500 to-purple-700',
    },
    {
      label: 'Fidelidade',
      href: '/fidelidade',
      icon: Gift,
      // Pastel rosa-coral (sem amber) pra atalho "presentes / cupons"
      sub: 'pontos e cupons',
      cor: 'from-rose-400 to-orange-500',
    },
    {
      label: 'Meus dados',
      href: '/lgpd',
      icon: ShieldCheck,
      sub: 'privacidade LGPD',
      cor: 'from-slate-500 to-slate-700',
    },
  ];

  const primeiroNome = dash.data?.nom_cliente?.split(' ')[0];
  const horaAtual = new Date().getHours();
  const periodoSaudacao = horaAtual < 12 ? 'Bom dia' : horaAtual < 18 ? 'Boa tarde' : 'Boa noite';
  const saudacaoCurta = primeiroNome
    ? `${periodoSaudacao}, ${primeiroNome} 👋`
    : `${periodoSaudacao} 👋`;
  const saudacaoLong = primeiroNome
    ? `${periodoSaudacao}, ${primeiroNome}!`
    : `${periodoSaudacao}!`;
  const subtituloFallback = dash.data?.qtd_lavagens_mes
    ? `${dash.data.qtd_lavagens_mes} lavagem(ns) este mês — vamos pra mais uma?`
    : 'Pronto pra sua próxima lavagem?';

  return (
    <div className="space-y-6 p-4 pb-24">
      {/* HERO: carrossel comercial OU fallback compacto */}
      <div className="octa-fade-in-up">
        <BannerCarousel
          saudacao={saudacaoCurta}
          fallback={<HeroFallback saudacao={saudacaoLong} subtitulo={subtituloFallback} />}
        />
      </div>

      {/* CTA principal — Lavar agora gigante (Apple Wallet vibes).
          Round 5: troca shadow-octa-lg+glow por shadow-brand-cta-hero + hover-cta-lift
          pra ficar consistente com BannerCarousel/HeroFallback (mesmo idioma protagonista). */}
      <Link
        to="/gerar-codigo"
        className="bg-octa-gradient shadow-brand-cta-hero hover-cta-lift octa-shimmer group relative block overflow-hidden rounded-3xl p-5 text-white"
        aria-label="Iniciar lavagem agora"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/15 blur-2xl"
        />
        <div className="relative z-10 flex items-center gap-4">
          <div className="octa-pulse-halo flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/20 ring-2 ring-white/40 backdrop-blur">
            <ScanLine className="h-7 w-7" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/85">
              Pronto pra começar
            </p>
            <p className="mt-0.5 text-2xl font-bold leading-tight">Lavar agora</p>
            <p className="mt-0.5 text-xs text-white/85">
              Gere o código e digite no totem em segundos.
            </p>
          </div>
          <ArrowRight className="h-6 w-6 shrink-0 transition-transform group-hover:translate-x-1" />
        </div>
      </Link>

      {/* KPIs visuais — 2 colunas compactas + cartão de assinatura */}
      <div className="grid grid-cols-2 gap-3">
        {dash.isLoading ? (
          <>
            <Skeleton className="h-24 rounded-2xl" />
            <Skeleton className="h-24 rounded-2xl" />
          </>
        ) : (
          <>
            <Card variant="elevated" className="octa-card-tap rounded-2xl">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wider">
                    Veículos
                  </p>
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300">
                    <CarFront className="h-3.5 w-3.5" />
                  </div>
                </div>
                <p className="text-octa-gradient mt-1.5 text-3xl font-bold tabular-nums leading-tight">
                  <CountUp value={dash.data?.qtd_veiculos ?? 0} />
                </p>
                <p className="text-muted-foreground text-xs">na sua frota</p>
              </CardContent>
            </Card>
            <Card variant="elevated" className="octa-card-tap rounded-2xl">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wider">
                    Este mês
                  </p>
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                    <Sparkles className="h-3.5 w-3.5" />
                  </div>
                </div>
                <p className="text-octa-gradient mt-1.5 text-3xl font-bold tabular-nums leading-tight">
                  <CountUp value={dash.data?.qtd_lavagens_mes ?? 0} />
                </p>
                <p className="text-muted-foreground text-xs">lavagens feitas</p>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Cartão Assinatura — Apple Wallet style */}
      {dash.data?.proximo_vencimento && (
        <Link to="/assinatura" className="group block" aria-label="Ver detalhes da assinatura">
          <Card
            variant="gradient"
            className="octa-scale-in octa-card-tap relative overflow-hidden rounded-3xl border-0"
          >
            <CardContent className="relative z-10 p-5">
              <div
                aria-hidden
                className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full bg-white/15 blur-3xl"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute -bottom-12 -left-8 h-36 w-36 rounded-full bg-white/10 blur-2xl"
              />
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/85">
                    Assinatura ativa
                  </p>
                  <p className="mt-1 text-xl font-bold leading-tight">
                    {dash.data.nom_plano ?? 'Plano atual'}
                  </p>
                </div>
                <TrendingUp className="h-5 w-5 opacity-80" />
              </div>
              <div className="mt-5 flex items-end justify-between">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/85">
                    Próximo vencimento
                  </p>
                  <p className="mt-0.5 text-sm font-semibold">
                    {formatarData(dash.data.proximo_vencimento)}
                  </p>
                </div>
                <p className="text-3xl font-extrabold leading-none">
                  {formatBrl(dash.data.val_proxima_cobranca ?? 0)}
                </p>
              </div>
              <div className="mt-4 flex items-center justify-between text-xs text-white/85">
                <span>Toque pra ver detalhes</span>
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </div>
            </CardContent>
          </Card>
        </Link>
      )}

      {/* Ações rápidas — grid 2 colunas visual.
          Round 5: cards usam octa-card-tap (lift+squash haptic) e entrada via
          motion stagger orquestrado em vez do octa-stagger CSS (mais previsível
          quando a query carrega tarde). */}
      <div>
        <h2 className="text-muted-foreground mb-3 text-[11px] font-semibold uppercase tracking-[0.18em]">
          Atalhos
        </h2>
        <motion.div
          className="grid grid-cols-2 gap-3"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
          }}
        >
          {acoes.map((a) => (
            <motion.div
              key={a.href}
              variants={{
                hidden: { opacity: 0, y: 12, scale: 0.96 },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] },
                },
              }}
            >
              <Link to={a.href} className="group block">
                <Card
                  variant="elevated"
                  className="octa-card-tap h-full overflow-hidden rounded-2xl"
                >
                  <CardContent className="space-y-2.5 p-4">
                    <div
                      className={`flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br ${a.cor} text-white shadow-md`}
                    >
                      <a.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold leading-tight">{a.label}</p>
                      <p className="text-muted-foreground mt-0.5 text-[11px]">{a.sub}</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}
